import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  MoreVert as MoreVertIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Message, ClassificationResult } from '@/types/api';
import { formatDateTime } from '@/utils/formatters';
import ClassificationBadge from './ClassificationBadge';
import { TelegramLinkButton } from '@/components/common/TelegramLinkButton';

interface MessageCardProps {
  message: Message;
  classification?: ClassificationResult;
  onViewContext?: (messageId: number) => void;
  onLinkToLibrary?: (messageId: number, messageText?: string, fileName?: string) => void;
  showActions?: boolean;
}

const MessageCard: React.FC<MessageCardProps> = ({
  message,
  classification,
  onViewContext,
  onLinkToLibrary,
  showActions = true,
}) => {
  const { t } = useTranslation('components');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const hasMedia = !!message.media;
  const hasText = !!message.message_text && message.message_text.trim() !== '';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewContext = () => {
    handleMenuClose();
    onViewContext?.(message.message_id);
  };

  const handleLinkToLibrary = () => {
    handleMenuClose();
    onLinkToLibrary?.(
      message.message_id,
      message.message_text || undefined,
      message.media?.file_name || undefined
    );
  };

  // Get media icon based on mime type
  const getMediaIcon = () => {
    if (!message.media) return <FileIcon />;

    const mimeType = message.media.mime_type || '';
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType === 'application/pdf') return <PdfIcon />;
    return <FileIcon />;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header: Classification, Message ID, Timestamp */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {classification && (
              <ClassificationBadge type={classification.type} />
            )}
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('messageCard.messageId', { messageId: message.message_id })}
            </Typography>
            {message.grouped_id && (
              <Chip label={t('messageCard.albumChip')} size="small" variant="outlined" sx={{ height: 20 }} />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(message.timestamp)}
            </Typography>
            <TelegramLinkButton chatId={message.chats_id} messageId={message.message_id} size="small" />
            {showActions && (onViewContext || onLinkToLibrary) && (
              <>
                <Tooltip title={t('messageCard.actionsTooltip')}>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {onViewContext && (
                    <MenuItem onClick={handleViewContext}>
                      <ListItemIcon>
                        <ViewIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('messageCard.viewContext')}</ListItemText>
                    </MenuItem>
                  )}
                  {onLinkToLibrary && (
                    <MenuItem onClick={handleLinkToLibrary}>
                      <ListItemIcon>
                        <LinkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('messageCard.linkToLibrary')}</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Message Text */}
        {hasText && (
          <Typography
            variant="body2"
            sx={{
              mb: hasMedia ? 1 : 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              direction: 'rtl', // RTL for Arabic text
              textAlign: 'right',
            }}
          >
            {message.message_text}
          </Typography>
        )}

        {/* Media Info */}
        {hasMedia && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {getMediaIcon()}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" fontWeight={600}>
                {message.media?.file_name || t('messageCard.unnamedFile')}
              </Typography>
              {message.media?.file_size && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {(message.media.file_size / 1024 / 1024).toFixed(2)} MB
                  {message.media.mime_type && ` • ${message.media.mime_type}`}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* No content indicator */}
        {!hasText && !hasMedia && (
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            {t('messageCard.emptyMessage')}
          </Typography>
        )}

        {/* Classification confidence (if available) */}
        {classification && classification.confidence && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('messageCard.confidence', { confidence: (classification.confidence * 100).toFixed(0) })}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MessageCard;
